import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { authFetch } from '../utils/auth';

export const THEMES = {
  'blue-water': {
    id: 'blue-water',
    name: 'Blue Water',
    swatch: 'linear-gradient(135deg, #0B1D26, #163B4E)',
    vars: {
      '--bg-deep': '#0B1D26',
      '--bg-mid': '#122E3C',
      '--bg-accent': '#163B4E',
      '--bg-end': '#0F2830',
      '--glow-a': 'rgba(42, 90, 63, 0.15)',
      '--glow-b': 'rgba(30, 91, 107, 0.12)',
      '--glow-c': 'rgba(212, 184, 150, 0.04)',
      '--accent': '#3D7B57',
      '--accent-hover': '#247A8E',
      '--accent-grad-start': '#2A5A3F',
      '--accent-grad-end': '#1E5B6B',
      '--text-primary': '#E4EDE8',
      '--text-muted': '#8FA8A0',
      '--warm': '#D4886B',
      '--sand': '#D4B896',
      '--glass-bg': 'rgba(255,255,255,0.05)',
      '--glass-border': 'rgba(255,255,255,0.08)',
      '--glass-hover': 'rgba(255,255,255,0.08)',
    }
  },
  'green-land': {
    id: 'green-land',
    name: 'Green Land',
    swatch: 'linear-gradient(135deg, #0F1E14, #1B3A22)',
    vars: {
      '--bg-deep': '#0F1E14',
      '--bg-mid': '#162C1C',
      '--bg-accent': '#1B3A22',
      '--bg-end': '#111F15',
      '--glow-a': 'rgba(60, 120, 50, 0.12)',
      '--glow-b': 'rgba(80, 140, 70, 0.08)',
      '--glow-c': 'rgba(200, 180, 120, 0.04)',
      '--accent': '#5A9E6F',
      '--accent-hover': '#6BB87F',
      '--accent-grad-start': '#3A7A4E',
      '--accent-grad-end': '#4A8A5E',
      '--text-primary': '#E0EDE4',
      '--text-muted': '#8DAF96',
      '--warm': '#C4A24E',
      '--sand': '#BCA86A',
      '--glass-bg': 'rgba(255,255,255,0.05)',
      '--glass-border': 'rgba(255,255,255,0.08)',
      '--glass-hover': 'rgba(255,255,255,0.08)',
    }
  },
  'red-warmth': {
    id: 'red-warmth',
    name: 'Red Warmth',
    swatch: 'linear-gradient(135deg, #1E0F0F, #3B1A1A)',
    vars: {
      '--bg-deep': '#1E0F0F',
      '--bg-mid': '#2C1616',
      '--bg-accent': '#3B1A1A',
      '--bg-end': '#1F1111',
      '--glow-a': 'rgba(140, 50, 40, 0.12)',
      '--glow-b': 'rgba(180, 90, 60, 0.08)',
      '--glow-c': 'rgba(200, 160, 100, 0.04)',
      '--accent': '#C0654A',
      '--accent-hover': '#D4785C',
      '--accent-grad-start': '#A04E38',
      '--accent-grad-end': '#B86040',
      '--text-primary': '#F0E4DF',
      '--text-muted': '#B09A90',
      '--warm': '#D4886B',
      '--sand': '#D4B896',
      '--glass-bg': 'rgba(255,255,255,0.05)',
      '--glass-border': 'rgba(255,255,255,0.08)',
      '--glass-hover': 'rgba(255,255,255,0.08)',
    }
  },
  'neutral-earth': {
    id: 'neutral-earth',
    name: 'Neutral Earth',
    swatch: 'linear-gradient(135deg, #1A1814, #302C24)',
    vars: {
      '--bg-deep': '#1A1814',
      '--bg-mid': '#24211B',
      '--bg-accent': '#302C24',
      '--bg-end': '#1C1A16',
      '--glow-a': 'rgba(120, 100, 60, 0.10)',
      '--glow-b': 'rgba(100, 80, 50, 0.08)',
      '--glow-c': 'rgba(180, 160, 120, 0.04)',
      '--accent': '#9E8A64',
      '--accent-hover': '#B09E76',
      '--accent-grad-start': '#7A6A48',
      '--accent-grad-end': '#8E7E58',
      '--text-primary': '#EDE8E0',
      '--text-muted': '#A8A090',
      '--warm': '#C4986B',
      '--sand': '#C8B48A',
      '--glass-bg': 'rgba(255,255,255,0.05)',
      '--glass-border': 'rgba(255,255,255,0.08)',
      '--glass-hover': 'rgba(255,255,255,0.08)',
    }
  }
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [currentTheme, setCurrentTheme] = useState('blue-water');
  const [customColors, setCustomColors] = useState(null);
  const [customBgImage, setCustomBgImage] = useState(null);
  const [userTier, setUserTier] = useState(null);

  const applyThemeVars = useCallback((themeId, custom) => {
    const theme = THEMES[themeId] || THEMES['blue-water'];
    const root = document.documentElement;

    Object.entries(theme.vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    if (custom) {
      Object.entries(custom).forEach(([key, value]) => {
        if (value) root.style.setProperty(key, value);
      });
    }
  }, []);

  const loadTheme = useCallback(async () => {
    try {
      const res = await authFetch('/api/theme');
      if (res.ok) {
        const data = await res.json();
        setCurrentTheme(data.theme_id || 'blue-water');
        setCustomColors(data.custom_colors || null);
        setCustomBgImage(data.custom_bg_image || null);
        applyThemeVars(data.theme_id || 'blue-water', data.custom_colors);
      }
    } catch (error) {
      applyThemeVars('blue-water', null);
    }
  }, [applyThemeVars]);

  const loadUserTier = useCallback(async () => {
    try {
      const res = await authFetch('/api/auth/me');
      if (res.ok) {
        const data = await res.json();
        setUserTier(data.subscription_tier || null);
      }
    } catch (error) {
      // Not logged in — expected on login page
    }
  }, []);

  useEffect(() => {
    loadTheme();
    loadUserTier();
  }, [loadTheme, loadUserTier]);

  const switchTheme = useCallback(async (themeId) => {
    setCurrentTheme(themeId);
    applyThemeVars(themeId, null);
    setCustomColors(null);
    setCustomBgImage(null);

    try {
      await authFetch('/api/theme', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme_id: themeId, custom_colors: null, custom_bg_image: null })
      });
    } catch (error) {
      toast.error('Could not save theme preference');
    }
  }, [applyThemeVars]);

  const saveCustomTheme = useCallback(async (colors, bgImage) => {
    setCustomColors(colors);
    setCustomBgImage(bgImage);
    applyThemeVars(currentTheme, colors);

    try {
      await authFetch('/api/theme', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ theme_id: currentTheme, custom_colors: colors, custom_bg_image: bgImage })
      });
    } catch (error) {
      toast.error('Could not save custom theme');
    }
  }, [applyThemeVars, currentTheme]);

  const contextValue = useMemo(() => ({
    currentTheme, customColors, customBgImage, userTier,
    switchTheme, saveCustomTheme, setUserTier, loadTheme
  }), [currentTheme, customColors, customBgImage, userTier, switchTheme, saveCustomTheme, loadTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
